/**
 * CoachSpeakButton — small "🔊 / ⏹" toggle for reading Coach Voice prose aloud.
 *
 * Self-contained: subscribes to the TTS singleton's speaking state so the
 * button's icon flips between play/stop without parent state plumbing.
 *
 * Props:
 *   text        The narrative text to read
 *   voice       Personality id ('mentor' | 'analyst' | 'drill' | 'friend')
 *   ttsEnabled  Master toggle from user settings
 *   ttsSettings Phase 16 — provider config from settings.coachLLM.tts. When
 *               omitted, the dispatcher defaults to the browser provider.
 *   darkMode
 */

import { useEffect, useState } from 'react';
import { Volume2, Square } from 'lucide-react';
import { speak, stop, subscribeToSpeakingState, isTTSAvailable } from '../../utils/coachTTS';

const CoachSpeakButton = ({
  text,
  voice = 'mentor',
  ttsEnabled = true,
  ttsSettings = null,
  darkMode = true,
}) => {
  const [speaking, setSpeaking] = useState(false);
  // Subscribe to the global "is speaking" flag — flips when ANY card's TTS
  // starts/stops, which is correct: the speaking state is shared across
  // providers (single-stream contract enforced by the dispatcher).
  useEffect(() => {
    return subscribeToSpeakingState(setSpeaking);
  }, []);

  // Hide entirely when TTS isn't available in this environment, OR when
  // settings disabled it. Keeps the UI clean for users who don't want it.
  if (!isTTSAvailable() || !ttsEnabled) return null;
  if (!text || typeof text !== 'string') return null;

  const handleClick = () => {
    if (speaking) {
      stop();
    } else {
      // speak() returns a Promise; we don't await — the speaking-state
      // subscription handles the visual flip back to play when audio ends.
      speak(text, { voice, ttsSettings });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={speaking ? 'Stop reading' : 'Read aloud'}
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${
        darkMode
          ? 'text-purple-300 hover:bg-purple-500/20 hover:text-purple-100'
          : 'text-purple-700 hover:bg-purple-200 hover:text-purple-900'
      }`}
      aria-label={speaking ? 'Stop reading' : 'Read aloud'}
    >
      {speaking ? <Square size={11} /> : <Volume2 size={11} />}
    </button>
  );
};

export default CoachSpeakButton;
